import {
  endOfDay,
  endOfMonth,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears
} from "date-fns";

export type AdminDateRangePreset =
  | "all"
  | "today"
  | "yesterday"
  | "last7"
  | "last30"
  | "thisMonth"
  | "lastMonth"
  | "thisYear"
  | "lastYear"
  | "custom";

export type AdminDateRangeValue = {
  preset: AdminDateRangePreset;
  from?: Date;
  to?: Date;
};

export const ADMIN_DATE_RANGE_PRESETS: {id: AdminDateRangePreset; label: string}[] = [
  {id: "all", label: "All time"},
  {id: "today", label: "Today"},
  {id: "yesterday", label: "Yesterday"},
  {id: "last7", label: "Last 7 days"},
  {id: "last30", label: "Last 30 days"},
  {id: "thisMonth", label: "This month"},
  {id: "lastMonth", label: "Last month"},
  {id: "thisYear", label: "This year"},
  {id: "lastYear", label: "Last year"},
  {id: "custom", label: "Custom"}
];

export const EMPTY_ADMIN_DATE_RANGE: AdminDateRangeValue = {preset: "all"};

export function resolveAdminDateRange(value: AdminDateRangeValue): {from?: Date; to?: Date} {
  const now = new Date();
  switch(value.preset) {
    case "all":
      return {};
    case "today":
      return {from: startOfDay(now), to: endOfDay(now)};
    case "yesterday": {
      const day = subDays(now, 1);
      return {from: startOfDay(day), to: endOfDay(day)};
    }
    case "last7":
      return {from: startOfDay(subDays(now, 6)), to: endOfDay(now)};
    case "last30":
      return {from: startOfDay(subDays(now, 29)), to: endOfDay(now)};
    case "thisMonth":
      return {from: startOfMonth(now), to: endOfDay(now)};
    case "lastMonth": {
      const month = subMonths(now, 1);
      return {from: startOfMonth(month), to: endOfMonth(month)};
    }
    case "thisYear":
      return {from: startOfYear(now), to: endOfDay(now)};
    case "lastYear": {
      const year = subYears(now, 1);
      return {from: startOfYear(year), to: endOfYear(year)};
    }
    case "custom":
      return {from: value.from, to: value.to};
    default:
      return {};
  }
}

export function adminDateRangeToApi(value: AdminDateRangeValue): {dateFrom?: string; dateTo?: string} {
  const {from, to} = resolveAdminDateRange(value);
  if(!from || !to) return {};
  return {
    dateFrom: format(from, "dd.MM.yyyy"),
    dateTo: format(to, "dd.MM.yyyy")
  };
}

export function adminDateRangeLabel(value: AdminDateRangeValue): string {
  if(value.preset !== "custom") {
    const preset = ADMIN_DATE_RANGE_PRESETS.find((p) => p.id === value.preset);
    if(preset) return preset.label;
  }
  const {from, to} = resolveAdminDateRange(value);
  if(from && to) {
    const left = format(from, "dd.MM.yyyy");
    const right = format(to, "dd.MM.yyyy");
    return left === right ? left : `${left} – ${right}`;
  }
  return "All time";
}
